import { useState } from 'react';
import { useLoaderData, useNavigate, useRevalidator } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { EmptyState } from '@ds/feedback';
import { Switch } from '@ds/forms';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { ACOES, GATILHOS } from '@/data/automacoesDaClinica';
import { dados } from '@/data/porta';
import type { AbaDeAutomacoes, Automacao, CargaDeAutomacoes } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { EnvioAutomatico } from './automacoes/EnvioAutomatico';

const ABAS: AbaDeAutomacoes[] = ['ativas', 'pausadas', 'todas'];

function abaDaRota(pathname: string): AbaDeAutomacoes {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeAutomacoes) : 'ativas';
}

export async function carregarAutomacoes({ request }: LoaderFunctionArgs): Promise<CargaDeAutomacoes> {
    return dados().automacoes(abaDaRota(new URL(request.url).pathname));
}

function quando(iso?: string): string {
    if (!iso) return 'nunca executou';
    return `última execução em ${new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })}`;
}

export function Automacoes() {
    const carga = useLoaderData() as CargaDeAutomacoes;
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const rota = rotaPorId('automacoes');
    const [emCurso, setEmCurso] = useState<string | null>(null);

    const alternar = async (a: Automacao, ativa: boolean) => {
        setEmCurso(a.id);
        try {
            await dados().alternarAutomacao(a.id, ativa);
            aviso({ title: ativa ? 'Automação ativada' : 'Automação pausada', description: a.nome });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível mudar a automação', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setEmCurso(null);
        }
    };

    const executar = async (a: Automacao) => {
        setEmCurso(a.id);
        try {
            const r = await dados().executarAutomacao(a.id);
            aviso({
                title: a.nome,
                description: r.repetidas ? `${r.descricao} · ${r.repetidas} já estavam abertas` : r.descricao,
                tone: r.criadas ? 'success' : 'info',
            });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível executar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setEmCurso(null);
        }
    };

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Automações"
                description="Gatilho e ação: o que a clínica quer que aconteça sozinho — e o que ainda depende de alguém."
            >
                <Tabs
                    ariaLabel="Situação das automações" activeId={carga.aba}
                    onChange={(id) => navigate(`/app/automacoes/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: carga.totais[c.path as keyof typeof carga.totais],
                    }))}
                />
            </PageHeader>

            <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                {carga.indicadores.map((i) => (
                    <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                ))}
            </div>

            {/* A tela diz de uma vez o que nenhum interruptor verde diria: se
                algo roda sozinho aqui, e por quê não. Sem isso, "ativa" seria lido
                como "está enviando", e a clínica confiaria num lembrete que não sai. */}
            <EnvioAutomatico canalConectado={carga.canalConectado} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {carga.automacoes.length === 0 && (
                    <Card padding={0}>
                        <EmptyState
                            icon="workflow" title="Nenhuma automação nesta aba"
                            description="Ative uma regra para vê-la aqui."
                        />
                    </Card>
                )}

                {carga.automacoes.map((a) => (
                    <Card key={a.id} padding={16}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                            <span
                                aria-hidden
                                style={{
                                    width: 34, height: 34, flex: '0 0 auto', borderRadius: 'var(--radius-sm)',
                                    background: a.ativa ? 'var(--surface-brand-soft)' : 'var(--surface-secondary)',
                                    color: a.ativa ? 'var(--text-brand)' : 'var(--neutral-500)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <Icon name="workflow" size={16} />
                            </span>

                            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 14, fontWeight: 600 }}>{a.nome}</span>
                                    <Badge tone={a.ativa ? 'success' : 'neutral'} dot>{a.ativa ? 'Ativa' : 'Pausada'}</Badge>
                                    {a.alcance !== undefined && a.alcance > 0 && (
                                        <Badge tone="info">{a.alcance} {a.alcanceRotulo}</Badge>
                                    )}
                                </div>

                                <p style={{ margin: '7px 0 0', fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                                    <strong>{GATILHOS[a.gatilho].rotulo}</strong> → {ACOES[a.acao].rotulo}
                                    <br />
                                    {GATILHOS[a.gatilho].descricao} · {quando(a.ultimaExecucaoEm)}
                                </p>

                                {a.bloqueio && (
                                    <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--warning-700, var(--text-secondary))' }}>
                                        {a.bloqueio}
                                    </p>
                                )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {a.executavel && (
                                    <Button
                                        size="sm" variant="secondary" disabled={emCurso === a.id}
                                        iconLeft={<Icon name="play" size={14} />}
                                        onClick={() => executar(a)}
                                    >
                                        Executar agora
                                    </Button>
                                )}
                                <Switch
                                    checked={a.ativa} disabled={emCurso === a.id}
                                    ariaLabel={`Ativar ${a.nome}`}
                                    onChange={(ligada) => alternar(a, ligada)}
                                />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}
